import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useCallback, useEffect, useRef } from 'react';
import {
  Modal as SemanticModal,
  type ModalProps as SemanticModalProps,
  ModalActions as SemanticModalActions,
  ModalContent as SemanticModalContent,
  ModalDescription as SemanticModalDescription,
  ModalDimmer as SemanticModalDimmer,
} from 'semantic-ui-react';
import { useTimeout } from 'usehooks-ts';

import type { ModalData, ModalType } from '@models';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  hideModal,
  selectActiveModal,
  selectActiveModalProps,
  selectIsModalVisible,
  selectIsMultipleActiveModals,
  showModal,
} from '@store/modals.slice';

import { ModalHeader } from './modal-header';
import s from './modal.scss';

export interface UseModalReturn<
  Type extends ModalType,
  Data extends ModalData<Type>,
> {
  id: Type;
  data?: Data;
  isVisible: boolean;
  isOpen: boolean;
  open: (props: Data extends void | null ? void : Data) => void;
  close: () => void;
}

/**
 * A hook to manage the state of a modal in the Redux store. Returns an object
 * containing the current state, data and methods to open and close the modal.
 */
export const useModal = <Type extends ModalType, Data extends ModalData<Type>>(
  type: Type,
): UseModalReturn<Type, Data> => {
  const dispatch = useAppDispatch();
  const instance = useAppSelector(selectActiveModal(type));
  const data = useAppSelector(selectActiveModalProps<Type, Data>(type));
  const isVisible = useAppSelector(selectIsModalVisible(type));

  return {
    id: type,
    data,
    isVisible,
    isOpen: !!instance,
    open: (props?: Data | void) => dispatch(showModal({ type, props })),
    close: () => dispatch(hideModal(type)),
  };
};

export interface ModalProps<T extends ModalType> extends Omit<
  SemanticModalProps,
  'onClose'
> {
  /**
   * A unique id for the modal instance. This should be unique across all modals
   * in the application, and will be used to manage the modal's state and data
   * in the Redux store.
   */
  id: T;
  /**
   * Stackable modals remain visible behind other modals that are launched on
   * top of them.
   */
  stackable?: boolean;

  /**
   * Called when the modal closes.
   * This event replaces Semantic's onClose event, which is not reliable
   */
  onAfterClose?: () => void;
}

const Modal = <T extends ModalType>({
  id,
  children,
  onAfterClose,
  stackable,
  className,
  ...props
}: ModalProps<T>) => {
  useStyles(s);

  const { isVisible, isOpen, close } = useModal(id);

  const isManyActive = useAppSelector(selectIsMultipleActiveModals);

  const closeModal = useCallback(() => {
    close();
  }, [close]);

  // Keep a ref to onAfterClose so effects always call the latest callback
  // without including it as a dependency (which would cause spurious cleanups).
  const onAfterCloseRef = useRef(onAfterClose);
  useEffect(() => {
    onAfterCloseRef.current = onAfterClose;
  });

  // Fire onAfterClose whenever the modal transitions from open to closed,
  // regardless of how it was closed (X button, dimmer, Cancel button, or
  // direct Redux dispatch). A prev-value ref skips the initial render when
  // isOpen is already false.
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = isOpenRef.current;
    isOpenRef.current = isOpen;
    if (wasOpen && !isOpen) {
      onAfterCloseRef.current?.();
    }
  }, [isOpen]);

  // Fallback: fire onAfterClose when the component unmounts while still open.
  // When a parent unmounts the Modal before Redux can set isOpen=false, the
  // isOpen transition effect never runs, so this cleanup covers that gap.
  // The isOpenRef guard prevents a double-fire on normal closes, where the
  // transition effect already ran and set isOpenRef to false before unmount.
  useEffect(
    () => () => {
      if (isOpenRef.current) {
        onAfterCloseRef.current?.();
      }
    },
    [],
  );

  // SUI's Modal does no focus management. Drive initial focus onto the
  // dialog so Tab stays within instead of escaping to page content.
  useTimeout(
    () => {
      const dialog = document.getElementById(id);
      if (!dialog) return;
      if (dialog.contains(document.activeElement)) return;
      dialog.focus();
    },
    isOpen ? 0 : null,
  );

  return (
    <SemanticModal
      id={id}
      role="dialog"
      tabIndex={-1}
      className={classNames(
        !isVisible && !stackable ? s.hiddenModal : '',
        className,
      )}
      style={{ visibility: isVisible ? 'visible' : 'hidden' }}
      open={isOpen}
      closeIcon={isVisible}
      dimmer={
        isManyActive && isVisible
          ? // If there are multiple active modals, apply a dimmer to the topmost
            // instance in case there are stackable instances underneath.
            true
          : // Otherwise this dimmer is transparent (i.e. use root dimmer)
            { style: { background: 'transparent' } }
      }
      onClose={closeModal}
      mountNode={process.env.BROWSER && document?.getElementById('rootDimmer')}
      {...props}
    >
      {children}
    </SemanticModal>
  );
};

export default Object.assign(Modal, {
  Actions: SemanticModalActions,
  Content: SemanticModalContent,
  Description: SemanticModalDescription,
  Dimmer: SemanticModalDimmer,
  Header: ModalHeader,
});
